import {
  Paging,
  RowFilterConditionOrNil,
  RowTypeBase,
  RowsIncrement,
  RowsSorting,
  SmartTableApi,
  apiColumnToTableColumn,
  sortingStateToSortDtoArray,
  ColumnFormat,
} from '@qre-mailing/smart-table';
import { ReactElement, useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useMutation } from '@tanstack/react-query';

import {
  postCommandCreateSampleMutation,
  postCommandDeleteSampleByDuplicityMutation,
  postCommandDeleteSampleMutation,
  postCommandUpdateSampleMutation,
  postCommandUpdateSampleRowMutation,
} from '../api/generated-evs/@tanstack/react-query.gen';
import {
  PostCommandCreateSampleData,
  PostCommandUpdateSampleResponse,
  postSampleRowsByQreIdSampleRowData,
  postSampleByQreIdStatus,
  getSampleRowsByQreIdSampleColumns,
  getSampleByQreIdSampleAutocomplete,
  getSurveyByQreIdQuotaFlatStructure,
  postCommandByQreIdFileExportSample,
} from '../api/generated-evs';
import type { QuotaFlatStructure } from '../api/hooks/queries/useQuotaFlatStructure';
import { useDeleteSampleConfirmDialog } from '../components/DeleteSampleConfirmDialog';
import { createTranslatedError, ErrorTranslations } from '../errors/transtatedErrors';
import { transformConditionSorting } from '../utils/sortingTransform';

import useSaveBlob from './useSaveBlob';
import { phoneColumnRenderer } from './columnRenderer/phoneColumnRenderer';
import { createQuotaColumnRenderer } from './columnRenderer/quotaColumnRenderer';

export type UsedRowType = RowTypeBase;

export type SamplesTableApi = SmartTableApi<UsedRowType> & {
  exportSampleApiCall: (condition: RowFilterConditionOrNil, sorting: RowsSorting) => Promise<void>;
};

export type UseSamplesTableApiResult = {
  api: SamplesTableApi;
  deleteConfirmDialog: ReactElement;
};

export const COLUMNS_TO_FILTER_IN_BATCH_EDIT = [
  'TelephoneNumber1',
  'TelephoneNumber2',
  'TelephoneNumber3',
  'AppointmentTelephoneNumber',
] as const;

const fileErrorTranslations: ErrorTranslations = defineMessages({
  'invalid-format': {
    id: 'error.invalid-format',
    defaultMessage: 'Tenhle soubor si s námi nerozumí. Přijímáme jen formáty .csv a .xlsx. Zkuste to znovu.',
  },
  'file-parse-error': {
    id: 'error.file-parse-error',
    defaultMessage: 'Chyba při zpracování souboru',
  },
  'file-empty': {
    id: 'error.file-empty',
    defaultMessage: 'Soubor je prázdný',
  },
  'missing-columns': {
    id: 'error.missing-columns',
    defaultMessage: 'V souboru chybí sloupce: {columns}',
  },
  'missing-variable-columns': {
    id: 'error.missing-variable-columns',
    defaultMessage: 'V souboru chybí alespoň jeden z těchto sloupců: {columns}',
  },
  'ipsos-id-empty': {
    id: 'error.ipsos-id-empty',
    defaultMessage: 'IpsosId je v jednom z řádků prázdný',
  },
  'duplicate-ipsos-ids': {
    id: 'error.duplicate-ipsos-ids',
    defaultMessage: 'V souboru jsou duplicitní IpsosId: {duplicateIds}',
  },
  'invalid-headers': {
    id: 'error.invalid-headers',
    defaultMessage: 'V souboru jsou nepovolené znaky v názvech sloupců: {invalidHeaders}',
  },
  'conflicting-headers': {
    id: 'error.conflicting-headers',
    defaultMessage:
      'Jejda — některé názvy sloupců ve souboru nejsou úplně stejné jako v projektu (záleží i na velkých a malých písmenech). Přejmenujte je podle dvojic (ve souboru → správně): {conflictingHeaders}',
  },
  'duplicate-headers': {
    id: 'error.duplicate-headers',
    defaultMessage: 'V souboru jsou duplicitní názvy sloupců (název - pozice): {duplicateHeaders}',
  },
  'survey-does-not-exists': {
    id: 'error.survey-does-not-exists',
    defaultMessage: 'Survey {qreId} neexistuje',
  },
  'ipsos-id-not-numeric': {
    id: 'error.ipsos-id-not-numeric',
    defaultMessage: 'IpsosId musí být číslo. Nesprávné IpsosId: {invalidIds}',
  },
  'banned-import-headers': {
    id: 'error.banned-import-headers',
    defaultMessage: 'V souboru jsou sloupce, které nelze importovat: {bannedImportHeaders}',
  },
  'banned-update-headers': {
    id: 'error.banned-update-headers',
    defaultMessage: 'V souboru jsou sloupce, které nelze upravit: {bannedUpdateHeaders}',
  },
  '400': {
    id: 'error.400',
    defaultMessage: 'Survey neexistuje',
  },
  default: {
    id: 'error.default',
    defaultMessage: 'Něco se pokazilo. Zkuste to znovu.',
  },
});

const addRowErrorTranslations: ErrorTranslations = defineMessages({
  'sample-row-already-exists': {
    id: 'error.sample-row-already-exists',
    defaultMessage:
      'Záznam s tímto IpsosId již existuje. Pokud chcete tento záznam upravit, použijte tlačítko "Upravit".',
  },
  'missing-columns': {
    id: 'error.missing-columns',
    defaultMessage: 'V souboru chybí sloupce: {columns}',
  },
  'missing-variable-columns': {
    id: 'error.missing-variable-columns',
    defaultMessage: 'V souboru chybí alespoň jeden z těchto sloupců: {columns}',
  },
  'all-phone-numbers-empty': {
    id: 'error.all-phone-numbers-empty',
    defaultMessage: 'Všechna telefonní čísla by po této změně byla prázdná',
  },
  'ipsos-id-not-numeric': {
    id: 'error.ipsos-id-not-numeric',
    defaultMessage: 'IpsosId musí být číslo. Nesprávné IpsosId: {invalidIds}',
  },
  'invalid-database': {
    id: 'error.invalid-database',
    defaultMessage: 'Neplatná nebo vypnutá databáze: {database}',
  },
  default: {
    id: 'error.default',
    defaultMessage: 'Něco se pokazilo. Zkuste to znovu.',
  },
});

export const useSamplesTableApi = (qreId: string): UseSamplesTableApiResult => {
  const intl = useIntl();
  const saveBlob = useSaveBlob();

  const { confirm: confirmResponseCodes, dialog: deleteConfirmDialog } = useDeleteSampleConfirmDialog();

  const addSampleMutation = useMutation(postCommandCreateSampleMutation());
  const deleteSampleMutation = useMutation(postCommandDeleteSampleMutation());
  const editSampleMutation = useMutation(postCommandUpdateSampleMutation());
  const editSampleRowMutation = useMutation(postCommandUpdateSampleRowMutation());
  const deleteSampleByDuplicityMutation = useMutation(postCommandDeleteSampleByDuplicityMutation());

  const filterCountApiCall = useCallback(
    async (condition: RowFilterConditionOrNil): Promise<{ total: number }> => {
      const conditionsGroup = {
        andConditions: condition?.andConditions ?? [],
        sorting: [] as Array<{ field: string; direction: 'ASC' | 'DESC' }>,
        ...(condition?.maxRecords && { maxRecords: condition.maxRecords }),
      };
      if (conditionsGroup.maxRecords == null) {
        delete (conditionsGroup as Record<string, unknown>).maxRecords;
      }

      const { data } = await postSampleByQreIdStatus({
        path: { qreId },
        body: { conditionsGroup },
        throwOnError: true,
      });
      return { total: data?.size ?? 0 };
    },
    [qreId]
  );

  const rowsApiCall = useCallback(
    async (
      condition: RowFilterConditionOrNil,
      sorting: RowsSorting,
      paging: Paging
    ): Promise<RowsIncrement<UsedRowType>> => {
      try {
        const conditionsGroup = {
          andConditions: condition?.andConditions ?? [],
          sorting: sortingStateToSortDtoArray(sorting),
          ...(condition?.maxRecords && { maxRecords: condition.maxRecords }),
        };

        // we need positive number
        if (conditionsGroup.maxRecords == null) {
          delete conditionsGroup.maxRecords;
        }

        const { data } = await postSampleRowsByQreIdSampleRowData({
          path: { qreId },
          body: {
            conditionsGroup,
            paging,
          },
        });
        const {
          records,
          totalRecords: total,
          lastUpdated,
        } = data || { records: [], totalRecords: 0, lastUpdated: null };

        const rows = records.map((record: unknown, i: number) => ({
          ...(record as Record<string, string>),
          ipsosId: (record as Record<string, string>).ipsosId,
          id: qreId,
          index: paging.offset + i + 1,
        }));

        return {
          rows: rows as unknown[] as UsedRowType[],
          total,
          lastUpdated,
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching sample rows:', error);
        return {
          rows: [],
          total: 0,
          lastUpdated: null,
        };
      }
    },
    [qreId]
  );

  const columnsDefinitionApiCall = useCallback(async () => {
    // const columns = await api.getFilterColumns({ qreId });
    // TODO: remove this after backend is updated
    const [columnsResult, quotaResult] = await Promise.all([
      getSampleRowsByQreIdSampleColumns({ path: { qreId } }),
      getSurveyByQreIdQuotaFlatStructure({ path: { qreId } }),
    ]);
    const { columnsData } = columnsResult.data || { columnsData: [] };
    const quotaLookup: QuotaFlatStructure = quotaResult.data ?? {};
    const quotaRenderer = createQuotaColumnRenderer(quotaLookup);

    // @ts-expect-error TODO fix this
    const tableColumns = columnsData.map((column) => apiColumnToTableColumn(column, intl));

    tableColumns.forEach((column) => {
      if (column.format === ColumnFormat.PHONE_NUMBER) {
        column.bodyCellRenderer = phoneColumnRenderer;
      } else if (quotaLookup[column.fieldName]) {
        column.bodyCellRenderer = quotaRenderer;
      }
    });

    return tableColumns;
  }, [intl, qreId]);

  const deleteRows = useCallback(
    async (
      rows: UsedRowType[],
      // @ts-expect-error TODO fix this
      condition
      // , condition: ConditionsGroupDto
    ) => {
      const ids = rows.map((i) => String(i.ipsosId));

      const transformedCondition = {
        ...transformConditionSorting(condition),
        ...(condition?.maxRecords && { maxRecords: condition.maxRecords }),
      };

      // we need positive number
      if (transformedCondition.maxRecords == null) {
        delete transformedCondition.maxRecords;
      }

      const hasIds = ids.length > 0;

      const response = await deleteSampleMutation?.mutateAsync({
        body: {
          condition: transformedCondition,
          qreId,
          ipsosIds: ids,
          ...(hasIds && { dryRun: true }),
        },
      });

      if (response && 'confirmationRequired' in response && response.confirmationRequired) {
        const confirmed = await confirmResponseCodes(response.responseCodeCounts ?? {});
        if (!confirmed) {
          return undefined;
        }

        const finalResponse = await deleteSampleMutation?.mutateAsync({
          body: {
            condition: transformedCondition,
            qreId,
            ipsosIds: ids,
            dryRun: false,
            deleteConfirmed: true,
          },
        });

        return finalResponse;
      }

      return response;
    },
    [deleteSampleMutation, qreId, confirmResponseCodes]
  );

  const singleEditApiCall = useCallback(
    async (
      // @ts-expect-error TODO fix this
      modifications,
      id: string
    ) => {
      try {
        const response = await editSampleRowMutation?.mutateAsync({
          body: {
            qreId: qreId,
            ipsosId: String(id),
            changes: modifications.map((i: { columnName: string; value: string }) => ({ [i.columnName]: i.value })),
          },
        });
        return response;
      } catch (error) {
        // @ts-expect-error type
        throw createTranslatedError(error, addRowErrorTranslations);
      }
    },
    [editSampleRowMutation, qreId]
  );

  const batchEditApiCall = useCallback(
    async (
      // @ts-expect-error TODO fix this
      modifications,
      // @ts-expect-error TODO fix this
      condition,
      ids: string[]
    ): // modifications: ModificationDto[], condition: ConditionsGroupDto, ids: string[]
    Promise<PostCommandUpdateSampleResponse> => {
      try {
        const transformedCondition = transformConditionSorting(condition);

        // we need positive number
        if (transformedCondition.maxRecords == null) {
          delete transformedCondition.maxRecords;
        }
        const transformedIds = ids.map((i) => String(i));

        const response = await editSampleMutation?.mutateAsync({
          body: {
            qreId,
            condition: transformedCondition,
            updates: {
              ipsosIds: transformedIds,
              changes: modifications.map((i: { columnName: string; value: string }) => ({ [i.columnName]: i.value })),
            },
          },
        });
        return response;
      } catch (error) {
        // @ts-expect-error type
        throw createTranslatedError(error, fileErrorTranslations);
      }
    },
    [editSampleMutation, qreId]
  );

  const addRowApiCall = useCallback(
    async (record: Record<string, string | boolean | number>) => {
      try {
        const response = await addSampleMutation?.mutateAsync({
          body: {
            qreId,
            rowData: record as unknown as NonNullable<PostCommandCreateSampleData['body']>['rowData'],
            rowColumns: Object.keys(record).filter((key) => record[key] !== undefined),
          },
        });
        return response;
      } catch (error) {
        // @ts-expect-error type
        throw createTranslatedError(error, addRowErrorTranslations);
      }
    },
    [addSampleMutation, qreId]
  );

  const autocompleteApiCall = useCallback(
    async (column: string, value: string) => {
      try {
        const { data } = await getSampleByQreIdSampleAutocomplete({
          path: { qreId },
          query: { column, prefix: value },
        });
        return data || [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Autocomplete API call failed:', error);
        return [];
      }
    },
    [qreId]
  );

  const deleteDuplicatesApiCall = useCallback(
    // @ts-expect-error TODO fix this
    async (condition) => {
      const transformedCondition = transformConditionSorting(condition);

      try {
        const response = await deleteSampleByDuplicityMutation?.mutateAsync({
          body: {
            qreId,
            condition: transformedCondition,
          },
        });

        return response;
      } catch (error) {
        // @ts-expect-error type
        throw createTranslatedError(error, fileErrorTranslations);
      }
    },
    [qreId, deleteSampleByDuplicityMutation]
  );

  const exportSampleApiCall = useCallback(
    async (condition: RowFilterConditionOrNil, sorting: RowsSorting) => {
      const conditionsGroup = {
        andConditions: condition?.andConditions ?? [],
        sorting: sortingStateToSortDtoArray(sorting),
        ...(condition?.maxRecords != null && { maxRecords: condition.maxRecords }),
      };
      if (conditionsGroup.maxRecords == null) {
        delete (conditionsGroup as Record<string, unknown>).maxRecords;
      }

      const result = await postCommandByQreIdFileExportSample({
        path: { qreId },
        body: { conditionsGroup },
        parseAs: 'blob',
        throwOnError: true,
      });
      const blob = result.data as Blob;
      const contentDisposition = result.response?.headers?.get?.('Content-Disposition') ?? '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
      const filename = filenameMatch?.[1]?.trim() ?? `sample-export-${qreId}.xlsx`;
      saveBlob(blob, filename);
    },
    [qreId, saveBlob]
  );

  const api = useMemo<SamplesTableApi>(
    () => ({
      createMailListApiCall: () => Promise.resolve(), // TODO remove from table api
      rowsApiCall,
      filterCountApiCall,
      columnsDefinitionApiCall,
      // @ts-expect-error deleteRows returns undefined when the user cancels the response-codes confirmation dialog
      deleteRows,
      addRowApiCall,
      batchEditApiCall,
      // @ts-expect-error TODO fix this
      singleEditApiCall,
      autocompleteApiCall,
      // @ts-expect-error TODO fix this
      deleteDuplicatesApiCall,
      exportSampleApiCall,
      columnsToFilterInBatchEdit: COLUMNS_TO_FILTER_IN_BATCH_EDIT,
    }),
    [
      rowsApiCall,
      filterCountApiCall,
      columnsDefinitionApiCall,
      deleteRows,
      batchEditApiCall,
      singleEditApiCall,
      addRowApiCall,
      autocompleteApiCall,
      deleteDuplicatesApiCall,
      exportSampleApiCall,
    ]
  );

  return useMemo(() => ({ api, deleteConfirmDialog }), [api, deleteConfirmDialog]);
};
